import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import '../styles/BookingModal.css';
import { getRoomTypes, getRoomTypesWithRatePlans, getEffectiveRoomMapping } from '../utils/roomTypeHelper';
import { getErrorMessage } from '../utils/errorMessages';
import PhoneInput from './PhoneInput';
import { PRICE_ENTRY_MODE, resolvePriceBreakdown, averagePerNight } from '../utils/vatPricing';

// Add days to a 'YYYY-MM-DD' string using LOCAL date parts (toISOString would shift the day
// in +ve timezones like Riyadh). Returns 'YYYY-MM-DD'.
const addDays = (str, n) => {
  const [y, m, d] = str.split('-').map(Number);
  const dt = new Date(y, m - 1, d + n);
  const p = (x) => String(x).padStart(2, '0');
  return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())}`;
};

const BookingModal = ({ show, onHide, onBookingCreated, userRole, preSelectedPropertyId, preSelectedProperty }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);

  const [formData, setFormData] = useState({
    propertyId: '',
    checkIn: '',
    checkOut: '',
    guestName: '',
    guestEmail: '',
    guestPhone: '',
    numberOfGuests: 1,
    // How ops states the price. Defaults to the way a direct deal is actually agreed —
    // one all-in figure — so the common case needs no mental arithmetic at the desk.
    priceEntryMode: PRICE_ENTRY_MODE.INCLUSIVE_TOTAL,
    stayTotalWithVat: '',  // Agreed total for the WHOLE stay, VAT included
    pricePerNight: '',     // VAT-EXCLUSIVE nightly rate (the other mode)
    vatPercentage: 15,  // Default 15% VAT
    paymentMethod: '',  // New: payment method tracking
    notes: '',
    roomTypeId: null,
    ratePlanId: null,  // Add rate plan for apartments
    // Commission tier — who supplied the demand. Default false = Host-Me-sourced, which
    // is what every booking is unless a person says otherwise.
    ownerSourced: false,
    sourcedByReason: ''
  });

  // Price calculation functions
  const calculateNights = () => {
    if (!formData.checkIn || !formData.checkOut) return 0;
    const checkInDate = new Date(formData.checkIn);
    const checkOutDate = new Date(formData.checkOut);
    const nights = Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24));
    return nights > 0 ? nights : 0;
  };

  // Whichever figure the operator typed, in whichever mode.
  const isNightlyMode = formData.priceEntryMode === PRICE_ENTRY_MODE.NIGHTLY_EXCLUSIVE;
  const enteredAmount = isNightlyMode ? formData.pricePerNight : formData.stayTotalWithVat;

  // ONE resolution of the money, shared by the breakdown on screen and the submitted
  // payload, so the two can never disagree. Rounding rule: utils/vatPricing.js.
  const breakdown = resolvePriceBreakdown({
    mode: formData.priceEntryMode,
    amount: enteredAmount,
    nights: calculateNights(),
    vatPercentage: formData.vatPercentage
  });

  useEffect(() => {
    if (show) {
      fetchProperties();
      resetForm();
      // If we have a pre-selected property, set it
      if (preSelectedPropertyId) {
        setFormData(prev => ({ ...prev, propertyId: preSelectedPropertyId }));
        // If we also have the full property data, use it directly
        if (preSelectedProperty) {
          setSelectedProperty(preSelectedProperty);
        }
      }
    }
  }, [show, preSelectedPropertyId, preSelectedProperty]);

  // Set selectedProperty when properties are loaded and we have a pre-selected ID
  useEffect(() => {
    if (preSelectedPropertyId && properties.length > 0 && !preSelectedProperty) {
      const property = properties.find(p => p._id === preSelectedPropertyId);
      if (property) {
        setSelectedProperty(property);
        setFormData(prev => ({ ...prev, propertyId: preSelectedPropertyId }));
      }
    }
  }, [preSelectedPropertyId, properties, preSelectedProperty]);

  useEffect(() => {
    if (formData.propertyId && !preSelectedProperty) {
      // Only look for property in list if we don't have a pre-selected one
      const property = properties.find(p => p._id === formData.propertyId);
      if (property) {
        setSelectedProperty(property);
        const roomTypes = getRoomTypes(property);

        // Auto-select room type for apartments (supports both single-unit and multi-unit)
        if (property.type === 'شقة') {
          // Use getEffectiveRoomMapping to support multi-unit properties
          const effectiveRoomMapping = getEffectiveRoomMapping(property);
          if (effectiveRoomMapping?.roomTypeId) {
            setFormData(prev => ({
              ...prev,
              roomTypeId: effectiveRoomMapping.roomTypeId,
              ratePlanId: null  // Reset rate plan when property changes
            }));
          } else {
            setFormData(prev => ({ ...prev, roomTypeId: null, ratePlanId: null }));
          }
        } else if (!roomTypes?.length) {
          setFormData(prev => ({ ...prev, roomTypeId: null, ratePlanId: null }));
        } else {
          // For hotels, reset room type and rate plan
          setFormData(prev => ({ ...prev, roomTypeId: null, ratePlanId: null }));
        }
      }
    }
  }, [formData.propertyId, properties, preSelectedProperty]);

  const fetchProperties = async () => {
    try {
      const token = localStorage.getItem('token');
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await axios.get(`${API_BASE}/api/properties`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      // Handle wrapped response format
      setProperties(response.data.properties || []);
    } catch (err) {
      console.error('Error fetching properties:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      propertyId: '',
      checkIn: '',
      checkOut: '',
      guestName: '',
      guestEmail: '',
      guestPhone: '',
      numberOfGuests: 1,
      priceEntryMode: PRICE_ENTRY_MODE.INCLUSIVE_TOTAL,
      stayTotalWithVat: '',
      pricePerNight: '',
      vatPercentage: 15,
      paymentMethod: '',
      notes: '',
      roomTypeId: null,
      ratePlanId: null,
      ownerSourced: false,
      sourcedByReason: ''
    });
    setSelectedProperty(null);
    setError(null);
    setSuccess(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      // Check-out must follow check-in: when check-in changes, snap an empty/invalid check-out
      // to the next day so its picker opens at the right month with a valid 1-night default.
      if (name === 'checkIn' && value && (!prev.checkOut || prev.checkOut <= value)) {
        next.checkOut = addDays(value, 1);
      }
      return next;
    });
  };

  const validateForm = () => {
    if (!formData.propertyId) {
      setError('يجب اختيار العقار');
      return false;
    }
    if (!formData.checkIn || !formData.checkOut) {
      setError('يجب تحديد تاريخ الوصول والمغادرة');
      return false;
    }
    if (new Date(formData.checkIn) >= new Date(formData.checkOut)) {
      setError('تاريخ المغادرة يجب أن يكون بعد تاريخ الوصول');
      return false;
    }
    if (!formData.guestName) {
      setError('يجب إدخال اسم الضيف');
      return false;
    }
    if (!(breakdown.total > 0)) {
      setError(isNightlyMode ? 'يجب إدخال سعر الليلة' : 'يجب إدخال إجمالي سعر الإقامة');
      return false;
    }
    if (formData.numberOfGuests < 1) {
      setError('عدد الضيوف يجب أن يكون 1 على الأقل');
      return false;
    }

    const checkInDate = new Date(formData.checkIn);
    const checkOutDate = new Date(formData.checkOut);
    const daysDiff = Math.ceil((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24));

    if (daysDiff > 365) {
      setError('فترة الحجز لا يمكن أن تتجاوز 365 يوم');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    // Move these outside try block to be accessible in catch
    const token = localStorage.getItem('token');
    const property = properties.find(p => p._id === formData.propertyId) || selectedProperty;

    // For apartments, ensure we have the roomTypeId even if it's auto-selected
    let actualRoomTypeId = formData.roomTypeId;
    if (property?.type === 'شقة' && !actualRoomTypeId) {
      const roomTypes = getRoomTypes(property);
      if (roomTypes?.length === 1) {
        actualRoomTypeId = roomTypes[0].id;
      }
    }

    // Amounts for submission — the SAME breakdown the operator just confirmed on screen.
    // In inclusive mode the agreed total is carried through untouched (vatPricing.js).
    const { subtotal: subtotalAmount, tax: taxAmount, total: totalAmount } = breakdown;

    // Map Arabic payment method to English enum value
    const paymentMethodMap = {
      'نقدي': 'cash',
      'تحويل بنكي': 'bank-transfer',
      'بطاقة ائتمان': 'credit-card',
      'مدى': 'mada',
      'أخرى': 'other'
    };

    const bookingData = {
      guestName: formData.guestName,
      guestEmail: formData.guestEmail || '',
      guestPhone: formData.guestPhone || '',
      checkIn: formData.checkIn,
      checkOut: formData.checkOut,
      numberOfGuests: parseInt(formData.numberOfGuests),
      totalAmount: totalAmount,  // Total with VAT (what guest pays)
      rate: {
        subtotal: subtotalAmount,  // Pre-VAT amount
        tax: taxAmount,            // VAT amount
        total: totalAmount         // Same as totalAmount
      },
      sourcePlatform: 'direct',
      notes: formData.notes || '',
      actualPaymentMethod: formData.paymentMethod ? paymentMethodMap[formData.paymentMethod] : undefined,
      roomTypeId: actualRoomTypeId || undefined,
      ratePlanId: formData.ratePlanId || undefined,
      // Sent on every create, never conditionally: an omitted field would leave the
      // server guessing, and the server's fallback and this default must agree.
      sourcedBy: formData.ownerSourced ? 'owner' : 'host_me',
      sourcedByReason: formData.ownerSourced ? (formData.sourcedByReason || '') : ''
    };

    try {
      const response = await axios.post(
        `/api/v1/properties-legacy/${formData.propertyId}/bookings`,
        bookingData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          resetForm();
          onHide();
          if (onBookingCreated) {
            onBookingCreated();
          }
        }, 1500);
      } else {
        setError(response.data.message || 'حدث خطأ في إنشاء الحجز');
      }
    } catch (err) {
      console.error('Error creating booking:', err);

      // Handle availability validation errors with conflict type awareness
      if (err.response?.status === 409) {
        const conflictType = err.response?.data?.conflictType;
        const canOverride = err.response?.data?.canOverride;
        const errorCode = err.response?.data?.errorCode;
        const errorData = err.response?.data?.errorData || {};

        // Get translated error message
        const translatedError = errorCode
          ? getErrorMessage(errorCode, errorData, 'ar')
          : err.response.data.error;

        // Never allow override when a PERSON is in the room — an existing booking, or a guest
        // relocated into this unit. Handled together because they refuse for the same reason and
        // the operator needs the same next step: pick something else, not try harder.
        if (conflictType === 'existing_booking') {
          setError('❌ ' + translatedError + '\n\nلا يمكن إنشاء حجز مزدوج. يرجى اختيار تواريخ أخرى.');
          return;
        }
        if (conflictType === 'guest_in_house') {
          setError('❌ ' + translatedError + '\n\nهذه الوحدة مشغولة بضيف تم نقله إليها. يرجى اختيار وحدة أخرى أو تواريخ أخرى.');
          return;
        }

        // For other conflicts, allow override if permitted
        if (canOverride) {
          const confirmOverride = window.confirm(
            `⚠️ تحذير: ${translatedError}\n\n` +
            'هل تريد المتابعة وإنشاء الحجز رغم ذلك؟\n' +
            '(هذا الخيار متاح للمدراء فقط)'
          );

        if (confirmOverride) {
          // Retry with forceBooking flag
          try {
            setError(null);
            const retryResponse = await axios.post(
              `/api/v1/properties-legacy/${formData.propertyId}/bookings`,
              { ...bookingData, forceBooking: true },
              {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              }
            );

            if (retryResponse.data.success) {
              setSuccess(true);
              setTimeout(() => {
                resetForm();
                onHide();
                if (onBookingCreated) {
                  onBookingCreated();
                }
              }, 1500);
            }
          } catch (retryErr) {
            setError(retryErr.response?.data?.message || 'حدث خطأ في إنشاء الحجز');
          }
        } else {
          // User chose not to override
          setError(err.response.data.message || err.response.data.error);
        }
        } else {
          // No override allowed for this conflict type
          setError(err.response?.data?.message || err.response?.data?.error || 'حدث خطأ في إنشاء الحجز');
        }
      } else {
        // Regular error (not a 409 conflict)
        setError(err.response?.data?.message || err.response?.data?.error || 'حدث خطأ في إنشاء الحجز');
      }
    } finally {
      setLoading(false);
    }
  };

  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      dir="rtl"
      className="booking-modal"
    >
      <Modal.Header closeButton className="booking-modal__header">
        <Modal.Title>حجز يدوي جديد</Modal.Title>
      </Modal.Header>

      <Modal.Body className="booking-modal__body">
        {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
        {success && <Alert variant="success" className="mb-3">تم إنشاء الحجز بنجاح!</Alert>}

        <Form onSubmit={handleSubmit}>
          <div className="section-block">
            <div className="section-title">بيانات العقار</div>

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>العقار *</Form.Label>
                  <Form.Select
                    name="propertyId"
                    value={formData.propertyId}
                    onChange={handleInputChange}
                    required
                    disabled={loading || !!preSelectedPropertyId}
                    className="touch-select"
                  >
                    <option value="">اختر العقار</option>
                    {/* If pre-selected, show that property */}
                    {preSelectedPropertyId && preSelectedProperty ? (
                      <option key={preSelectedProperty._id} value={preSelectedProperty._id}>
                        {preSelectedProperty.name} - {preSelectedProperty.address || ''}
                      </option>
                    ) : (
                      /* Otherwise show all properties */
                      properties.map(property => (
                        <option key={property._id} value={property._id}>
                          {property.name} - {property.address}
                        </option>
                      ))
                    )}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* For Hotels: Show room type dropdown */}
            {selectedProperty?.type === 'فندق' && getRoomTypes(selectedProperty)?.length > 0 && (
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>نوع الغرفة</Form.Label>
                    <Form.Select
                      name="roomTypeId"
                      value={formData.roomTypeId || ''}
                      onChange={handleInputChange}
                      disabled={loading}
                      className="touch-select"
                    >
                      <option value="">اختر نوع الغرفة (اختياري)</option>
                      {getRoomTypes(selectedProperty).map(roomType => (
                        <option key={roomType.id} value={roomType.id}>
                          {roomType.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            )}

            {/* For Apartments: Show rate plan dropdown (room type is auto-selected) */}
            {selectedProperty?.type === 'شقة' && (() => {
              // Use getEffectiveRoomMapping to support both single-unit and multi-unit properties
              const effectiveRoomMapping = getEffectiveRoomMapping(selectedProperty);
              const ratePlans = effectiveRoomMapping?.ratePlans || [];

              // Get room type name for display
              const roomTypes = getRoomTypes(selectedProperty);
              const roomType = roomTypes.find(rt => rt.id === effectiveRoomMapping?.roomTypeId);
              const roomTypeName = roomType?.name || selectedProperty?.type || 'شقة';

              return (
                <Row className="mb-3">
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label>خطة السعر</Form.Label>
                      <Form.Select
                        name="ratePlanId"
                        value={formData.ratePlanId || ''}
                        onChange={handleInputChange}
                        disabled={loading}
                        className="touch-select"
                      >
                        <option value="">اختر خطة السعر</option>
                        {/* Get rate plans using effective room mapping (supports multi-unit via linkedRoomTypeId) */}
                        {ratePlans.map(ratePlan => (
                          <option key={ratePlan.id} value={ratePlan.id}>
                            {ratePlan.name || 'Standard Rate'}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">
                        نوع الغرفة: {roomTypeName} (تم الاختيار تلقائيًا)
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              );
            })()}
          </div>

          <div className="section-block">
            <div className="section-title">تواريخ الحجز</div>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>تاريخ الوصول *</Form.Label>
                  <Form.Control
                    type="date"
                    name="checkIn"
                    value={formData.checkIn}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    className="touch-input"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>تاريخ المغادرة *</Form.Label>
                  <Form.Control
                    type="date"
                    name="checkOut"
                    min={formData.checkIn ? addDays(formData.checkIn, 1) : undefined}
                    value={formData.checkOut}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    className="touch-input"
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>

          <div className="section-block">
            <div className="section-title">بيانات الضيف والتسعير</div>

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>اسم الضيف *</Form.Label>
                  <Form.Control
                    type="text"
                    name="guestName"
                    value={formData.guestName}
                    onChange={handleInputChange}
                    placeholder="أدخل اسم الضيف"
                    required
                    disabled={loading}
                    className="touch-input"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>البريد الإلكتروني</Form.Label>
                  <Form.Control
                    type="email"
                    name="guestEmail"
                    value={formData.guestEmail}
                    onChange={handleInputChange}
                    placeholder="example@email.com"
                    disabled={loading}
                    className="touch-input"
                  />
                  <Form.Text className="text-muted">
                    سيتم إرسال تأكيد الحجز للضيف عبر البريد الإلكتروني
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={8}>
                <Form.Group>
                  <Form.Label>رقم الهاتف</Form.Label>
                  <PhoneInput
                    value={formData.guestPhone}
                    defaultCountry="SA"
                    onChange={(phone) => setFormData(prev => ({ ...prev, guestPhone: phone }))}
                    disabled={loading}
                  />
                  <Form.Text className="text-muted">
                    📌 رقم الهاتف يُستخدم لربط الحجز بملف الضيف — تأكد من إدخاله بشكل صحيح
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {/* ── Price basis ──────────────────────────────────────────────
                Direct deals are agreed as one all-in figure ("3,000 including VAT"),
                so that is the default. The basis is stated on every label below rather
                than remembered: one mode is VAT-inclusive and the other is not. */}
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group role="radiogroup" aria-labelledby="price-mode-label">
                  <Form.Label id="price-mode-label">طريقة إدخال السعر</Form.Label>
                  <div>
                    <Form.Check
                      inline
                      type="radio"
                      id="price-mode-inclusive"
                      name="priceEntryMode"
                      label="الإجمالي (شامل الضريبة)"
                      disabled={loading}
                      checked={!isNightlyMode}
                      onChange={() => setFormData(prev => ({
                        ...prev,
                        priceEntryMode: PRICE_ENTRY_MODE.INCLUSIVE_TOTAL,
                        pricePerNight: ''
                      }))}
                    />
                    <Form.Check
                      inline
                      type="radio"
                      id="price-mode-nightly"
                      name="priceEntryMode"
                      label="سعر الليلة (قبل الضريبة)"
                      disabled={loading}
                      checked={isNightlyMode}
                      onChange={() => setFormData(prev => ({
                        ...prev,
                        priceEntryMode: PRICE_ENTRY_MODE.NIGHTLY_EXCLUSIVE,
                        stayTotalWithVat: ''
                      }))}
                    />
                  </div>
                  <Form.Text className="text-muted">
                    {isNightlyMode
                      ? 'تُضاف الضريبة فوق سعر الليلة'
                      : 'أدخل المبلغ المتفق عليه مع الضيف كما هو — الضريبة محتسبة بداخله'}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>عدد الضيوف *</Form.Label>
                  <Form.Control
                    type="number"
                    name="numberOfGuests"
                    value={formData.numberOfGuests}
                    onChange={handleInputChange}
                    min="1"
                    required
                    disabled={loading}
                    className="touch-input"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>
                    {isNightlyMode
                      ? 'سعر الليلة قبل الضريبة (ريال) *'
                      : 'إجمالي الإقامة شامل الضريبة (ريال) *'}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name={isNightlyMode ? 'pricePerNight' : 'stayTotalWithVat'}
                    value={enteredAmount}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    required
                    disabled={loading}
                    className="touch-input"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>نسبة الضريبة (VAT %)</Form.Label>
                  <Form.Select
                    name="vatPercentage"
                    value={formData.vatPercentage}
                    onChange={handleInputChange}
                    disabled={loading}
                    className="touch-select"
                  >
                    <option value="15">15% (القيمة المضافة)</option>
                    <option value="0">بدون ضريبة (0%)</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    {formData.vatPercentage == 0 ? 'لا توجد ضريبة' : `ضريبة ${formData.vatPercentage}%`}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </div>

          {/* Price Breakdown Display */}
          {formData.checkIn && formData.checkOut && breakdown.total > 0 && (
            <div className="section-block">
              <div className="section-title">المعلومات المالية</div>
              <div className="financial-summary plain" dir="rtl">
                <div className="summary-row-line">
                  <span className="summary-label">الإجمالي (شامل الضريبة)</span>
                  <span className="summary-value text-success">{breakdown.total.toFixed(2)} ريال</span>
                </div>
                <div className="summary-row-line">
                  <span className="summary-label">
                    {formData.vatPercentage == 0 ? 'الضريبة (بدون)' : `الضريبة (${formData.vatPercentage}%)`}
                  </span>
                  <span className="summary-value">{breakdown.tax.toFixed(2)} ريال</span>
                </div>
                <div className="summary-row-line">
                  <span className="summary-label">قبل الضريبة</span>
                  <span className="summary-value">{breakdown.subtotal.toFixed(2)} ريال</span>
                </div>
                <div className="summary-row-line">
                  <span className="summary-label">عدد الليالي</span>
                  <span className="summary-value">{calculateNights()} ليلة</span>
                </div>
                {/* Informational ONLY — an average of a negotiated stay total, never a
                    rate anybody agreed to per night, and never fed back into a price. */}
                {!isNightlyMode && calculateNights() > 0 && (
                  <div className="summary-row-line">
                    <span className="summary-label">المتوسط لليلة (شامل الضريبة)</span>
                    <span className="summary-value text-muted">
                      {averagePerNight(breakdown.total, calculateNights()).toFixed(2)} ريال
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="section-block">
            <div className="section-title">الدفع والملاحظات</div>

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>طريقة الدفع</Form.Label>
                  <Form.Select
                    name="paymentMethod"
                    value={formData.paymentMethod}
                    onChange={handleInputChange}
                    disabled={loading}
                    className="touch-select"
                  >
                    <option value="">اختر طريقة الدفع (اختياري)</option>
                    <option value="نقدي">نقدي</option>
                    <option value="تحويل بنكي">تحويل بنكي</option>
                    <option value="بطاقة ائتمان">بطاقة ائتمان</option>
                    <option value="مدى">مدى</option>
                    <option value="أخرى">أخرى</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* ── Commission tier ──────────────────────────────────────────
                Ticked only when the OWNER found this guest independently. It decides
                which commission rate the booking is billed at, so it is deliberately
                unticked by default and never inferred from the platform. */}
            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Check
                    type="checkbox"
                    id="booking-owner-sourced"
                    name="ownerSourced"
                    checked={formData.ownerSourced}
                    disabled={loading}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      ownerSourced: e.target.checked,
                      sourcedByReason: e.target.checked ? prev.sourcedByReason : ''
                    }))}
                    label="حجز وفّره المالك بنفسه"
                  />
                  <Form.Text className="text-muted">
                    يُحتسب بنسبة الرسوم المتفق عليها مع المالك، إن وُجدت.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {formData.ownerSourced && (
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>كيف وصل هذا الضيف؟ (اختياري)</Form.Label>
                    <Form.Control
                      name="sourcedByReason"
                      value={formData.sourcedByReason}
                      onChange={handleInputChange}
                      placeholder="مثال: طالب تواصل مع المالك مباشرة"
                      disabled={loading}
                      className="touch-input"
                    />
                    <Form.Text className="text-muted">
                      يُسجَّل في سجل التغييرات في الحجز.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            )}

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>ملاحظات</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="أي ملاحظات إضافية..."
                    disabled={loading}
                    className="touch-input"
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>
        </Form>
      </Modal.Body>

      <Modal.Footer className="touch-modal-actions">
        <Button variant="secondary" onClick={onHide} disabled={loading} className="touch-btn">
          إلغاء
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={loading}
          className="touch-btn"
        >
          {loading ? 'جاري الإنشاء...' : 'إنشاء الحجز'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BookingModal;
